import { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateContractModal } from "@/pages/contracts/CreateContractModal";
import { EditContractModal } from "@/pages/contracts/EditContractModal";
import {
  ContractCreditBadge,
  ContractStatusBadge,
  ContractTypeBadge,
  contractAmountLabel,
} from "@/pages/contracts/contractDisplay";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { billingIntervalLabel } from "@/lib/contractBilling";
import { CONTRACT_TYPES } from "@/lib/contractType";
import type { Contract, ContractType } from "@/types/camping";

type StatusFilter = "" | "active" | "notice" | "terminated";
type TypeFilter = "" | ContractType;

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: "active", label: "Aktiv" },
  { key: "notice", label: "Gekündigt" },
  { key: "terminated", label: "Beendet" },
  { key: "", label: "Alle" },
];

export function ContractsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Contract | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<Contract[]>("/api/v1/contracts")
      .then(setContracts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Verträge",
      subtitle: "Dauercamper, Abstellplätze und Abrechnungen",
    });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  const statusCounts = useMemo(() => {
    const inType = typeFilter
      ? contracts.filter((c) => c.contract_type === typeFilter)
      : contracts;
    return {
      active: inType.filter((c) => c.status === "active").length,
      notice: inType.filter((c) => c.status === "notice").length,
      terminated: inType.filter((c) => c.status === "terminated").length,
      all: inType.length,
    };
  }, [contracts, typeFilter]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return contracts.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (typeFilter && c.contract_type !== typeFilter) return false;
      if (!query) return true;
      return [c.guest_name, c.pitch_code, c.booking_number, c.special_terms]
        .some((value) => (value ?? "").toLowerCase().includes(query));
    });
  }, [contracts, search, statusFilter, typeFilter]);

  const emptyMessage =
    contracts.length === 0
      ? "Noch keine Verträge."
      : search.trim()
        ? "Keine Verträge für diese Suche."
        : "Keine Verträge in dieser Ansicht.";

  const countFor = (key: StatusFilter) => {
    if (key === "active") return statusCounts.active;
    if (key === "notice") return statusCounts.notice;
    if (key === "terminated") return statusCounts.terminated;
    return statusCounts.all;
  };

  return (
    <div className="space-y-4">
      <EditContractModal
        open={!!selected}
        contract={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canEdit}
        userGroups={groups}
      />
      <CreateContractModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <Table
        wide
        isEmpty={loading || visible.length === 0}
        emptyMessage={loading ? "Verträge werden geladen…" : emptyMessage}
        toolbar={
          <div className="flex flex-col gap-3 border-b border-separator px-4 py-3 sm:px-5">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="overflow-x-auto">
                  <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Status">
                    {STATUS_TABS.map((tab) => (
                      <button
                        key={tab.key || "all"}
                        type="button"
                        role="tab"
                        aria-selected={statusFilter === tab.key}
                        className={clsx(
                          "eh-segmented-item inline-flex items-center gap-1.5",
                          statusFilter === tab.key && "is-active",
                        )}
                        onClick={() => setStatusFilter(tab.key)}
                      >
                        {tab.label}
                        <span className="tabular-nums text-[0.6875rem] font-semibold text-label-tertiary">
                          {countFor(tab.key)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Vertragsart">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={typeFilter === ""}
                      className={clsx("eh-segmented-item", typeFilter === "" && "is-active")}
                      onClick={() => setTypeFilter("")}
                    >
                      Alle Arten
                    </button>
                    {CONTRACT_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        role="tab"
                        aria-selected={typeFilter === type.value}
                        className={clsx("eh-segmented-item", typeFilter === type.value && "is-active")}
                        onClick={() => setTypeFilter(type.value)}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative min-w-[12rem] sm:w-64">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-label-tertiary"
                    aria-hidden
                  />
                  <input
                    type="search"
                    className="eh-input w-full pl-9"
                    placeholder="Gast, Platz oder Buchung"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Verträge suchen"
                    autoComplete="off"
                  />
                </div>
                {canEdit ? (
                  <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
                    Neuer Vertrag
                  </Button>
                ) : null}
              </div>
            </div>
            <p className="text-xs text-label-tertiary">
              {loading
                ? "Verträge werden geladen…"
                : `${visible.length} ${visible.length === 1 ? "Vertrag" : "Verträge"} · Zeile öffnet die Details`}
            </p>
          </div>
        }
      >
        <thead>
          <tr>
            <th>Gast</th>
            <th>Platz</th>
            <th>Laufzeit</th>
            <th>Abrechnung</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((c) => (
            <ClickableTableRow
              key={c.id}
              ariaLabel={`Vertrag ${c.guest_name ?? c.pitch_code ?? ""} öffnen`}
              onActivate={() => setSelected(c)}
            >
              <td data-label="Gast">
                <div className="font-medium">{c.guest_name ?? "—"}</div>
                <div className="mt-1">
                  <ContractTypeBadge type={c.contract_type} />
                </div>
              </td>
              <td data-label="Platz" className="font-medium tabular-nums">
                {c.pitch_code ?? "—"}
              </td>
              <td data-label="Laufzeit">
                <div className="tabular-nums">
                  {formatIsoDateShort(c.start_date)}
                  {" – "}
                  {c.end_date ? formatIsoDateShort(c.end_date) : "offen"}
                </div>
                {c.status === "notice" && c.cancellation_effective_on ? (
                  <div className="mt-0.5 text-xs text-status-orange">
                    gekündigt zum {formatIsoDateShort(c.cancellation_effective_on)}
                  </div>
                ) : c.auto_renew ? (
                  <div className="mt-0.5 text-xs text-label-tertiary">verlängert sich</div>
                ) : null}
                {c.superseded_on ? (
                  <div className="mt-0.5 text-xs text-label-secondary">
                    abgelöst zum {formatIsoDateShort(c.superseded_on)}
                  </div>
                ) : null}
              </td>
              <td data-label="Abrechnung">
                <div>{billingIntervalLabel(c.billing_interval)}</div>
                <div className="mt-0.5 tabular-nums text-label-secondary">{contractAmountLabel(c)}</div>
                {!c.billing_period_amount_gross && !c.monthly_amount && c.resolved_period_amount_gross ? (
                  <div className="text-xs text-label-tertiary">aus Zuordnung</div>
                ) : null}
                {c.special_terms?.trim() ? (
                  <p
                    className="mt-1 line-clamp-2 max-w-xs text-xs text-label-secondary"
                    title={c.special_terms}
                  >
                    <span className="font-medium text-status-orange">Sonderkonditionen: </span>
                    {c.special_terms}
                  </p>
                ) : null}
              </td>
              <td data-label="Status">
                <div className="flex flex-wrap items-center gap-1.5">
                  <ContractStatusBadge status={c.status} />
                  <ContractCreditBadge contract={c} />
                </div>
              </td>
            </ClickableTableRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
